import { format } from 'date-fns'
import { nb } from 'date-fns/locale'
import type { Gallery } from '../../lib/types'
import type { SignedUrlVariants } from '../../lib/api'
import Collection from './Collection'
import Item from './Item'

interface CollectionGridProps {
  gallery: Gallery
  onItemClick: (itemId: string) => void
  editMode: boolean
  onDeleteItem: (itemId: string, collectionId: string, storagePath?: string) => Promise<void>
  onDeleteCollection: (collectionId: string) => Promise<void>
  deletingItemIds: Set<string>
  signedUrls: Map<string, SignedUrlVariants>
}

export default function CollectionGrid({ gallery, onItemClick, editMode, onDeleteItem, onDeleteCollection, deletingItemIds, signedUrls }: CollectionGridProps) {
  const totalItems = gallery.collections.reduce((sum, c) => sum + c.items.length, 0)

  // Single item: fill the entire viewport with metadata overlay — no viewer
  if (totalItems === 1) {
    const collection = gallery.collections[0]
    const item = collection.items[0]
    const dateStr = item.createdAt
      ? format(new Date(item.createdAt), 'd. MMMM yyyy', { locale: nb })
      : null
    const cameraInfo = item.metadata?.camera

    return (
      <div className="h-dvh w-full relative">
        <Item
          item={item}
          onClick={() => {}}
          fullscreen
          signedUrl={signedUrls.get(item.id)?.full}
        />
        <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/60 to-transparent pointer-events-none">
          {collection.title && <p className="text-white/70 text-sm font-light">{collection.title}</p>}
          <p className="text-white text-lg font-medium">{collection.username}</p>
          {dateStr && <p className="text-white/80 text-sm">{dateStr}</p>}
          {cameraInfo && <p className="text-white/50 text-xs mt-0.5">{cameraInfo}</p>}
        </div>
      </div>
    )
  }

  return (
    <div className="p-1 w-full">
      <div className="flex flex-col gap-5">
        {gallery.collections.map((collection) => (
          <Collection
            key={collection.id}
            collection={collection}
            onItemClick={onItemClick}
            editMode={editMode}
            onDeleteItem={onDeleteItem}
            onDeleteCollection={onDeleteCollection}
            deletingItemIds={deletingItemIds}
            signedUrls={signedUrls}
          />
        ))}
      </div>
    </div>
  )
}
